// Wartungsmeldung für beide SPAs (meterreading + landpages).
//
// Gesteuert über den optionalen Block `maintenance` je Umgebung in
// `public/cos-config.json` — also ohne Rebuild, durch Bearbeiten der
// Server-Datei (wie der Anyline-Key). Der Service Worker holt
// cos-config.json Netz-zuerst, eine Änderung wirkt beim nächsten Seitenaufruf.
//
//   "maintenance": {
//     "enabled": true,
//     "start": "2026-10-20T06:00",   // optional, Lokalzeit
//     "end": "2026-10-20T18:00",     // optional, Lokalzeit
//     "message": "",                 // optional, sonst DEFAULT_MESSAGE
//     "bypassKey": "",               // optional, siehe unten
//     "exemptTypes": []              // optional, z. B. ["CommonIntern"]
//   }
//
// Aktiv ist die Meldung, wenn `enabled` true ist und die aktuelle Zeit im
// Fenster [start, end) liegt. Fehlt start, gilt sie sofort; fehlt end, bis
// `enabled` wieder false ist (z. B. vor dem Go-live). Ein gesetztes end wird
// dem Nutzer als "voraussichtlich bis …" angezeigt.
//
// Ausgewertet wird beim Laden der Seite. Eine laufende Erfassung wird beim
// Erreichen von start bewusst NICHT abgebrochen; erreicht end, während die
// Meldung angezeigt wird, lädt die Seite neu (useMaintenance).
//
// bypassKey: Wer die SPA mit `?maintenanceBypass=<bypassKey>` aufruft, sieht
// statt der Meldung die normale Anwendung (für Abnahmetests auf PROD vor dem
// Go-live). Der Schlüssel bleibt für die Browser-Session gemerkt. Er ist
// kein Zugriffsschutz — die Anwendung ist dahinter so offen wie sonst auch.
//
// exemptTypes: meterreading-Typen (type-Parameter, Groß-/Kleinschreibung
// egal), die trotz Wartung normal laufen — z. B. die interne Erfassung
// (CommonIntern) der JVA-Support-Mitarbeiter, während das öffentliche
// Formular noch vorgeschaltet ist. Die landpages kennen keinen solchen
// Typ; dort bleibt nur die BFSG-Erklärung immer erreichbar.
//
// Ungültige Datumswerte machen den Block wirkungslos (console.error): eine
// vertippte Zeit darf das Portal nicht unbefristet sperren.

export interface MaintenanceConfig {
  enabled: boolean;
  start?: Date;
  end?: Date;
  message?: string;
  bypassKey?: string;
  /** Kleingeschriebene meterreading-Typen, die von der Wartung ausgenommen sind. */
  exemptTypes?: string[];
}

export interface MaintenanceState {
  active: boolean;
  message: string;
  /** Voraussichtliches Ende, nur wenn konfiguriert. */
  end?: Date;
  /** Kleingeschriebene Typen, für die die Meldung nicht gilt. */
  exemptTypes: string[];
}

export const DEFAULT_MESSAGE =
  "Das Zählerstandsportal steht wegen Wartungsarbeiten vorübergehend " +
  "nicht zur Verfügung. Bitte versuchen Sie es später erneut.";

export const BYPASS_PARAM = "maintenanceBypass";
const BYPASS_STORAGE_KEY = "cos.maintenanceBypass";

/**
 * Liest den Rohblock aus cos-config.json. `undefined` = kein (wirksamer)
 * Wartungsblock. Wirft nie: ein kaputter Block soll die App nicht am Start
 * hindern, sondern nur geloggt werden.
 */
export function parseMaintenance(
  raw: unknown,
  section: string
): MaintenanceConfig | undefined {
  if (raw === undefined || raw === null) return undefined;
  const fail = (why: string): undefined => {
    console.error(
      `[ene] cos-config.json: "${section}.maintenance" wird ignoriert — ${why}`
    );
    return undefined;
  };
  if (typeof raw !== "object" || Array.isArray(raw)) {
    return fail("kein Objekt.");
  }
  const m = raw as Record<string, unknown>;
  if (typeof m.enabled !== "boolean") {
    return fail('"enabled" muss true oder false sein.');
  }

  const parseDate = (key: "start" | "end"): Date | undefined | null => {
    const v = m[key];
    if (v === undefined || v === null || v === "") return undefined;
    if (typeof v !== "string") return null;
    // Nur ISO-Form mit "T" (z. B. 2026-10-20T06:00) — Safari scheitert an
    // der Leerzeichen-Variante, ohne Zonenangabe gilt Lokalzeit.
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const start = parseDate("start");
  const end = parseDate("end");
  if (start === null) return fail('"start" ist kein Datum der Form 2026-10-20T06:00.');
  if (end === null) return fail('"end" ist kein Datum der Form 2026-10-20T18:00.');
  if (start && end && end <= start) return fail('"end" liegt nicht nach "start".');

  const str = (key: string): string | undefined => {
    const v = m[key];
    return typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
  };

  const ex = m.exemptTypes;
  if (ex !== undefined && (!Array.isArray(ex) || ex.some((t) => typeof t !== "string"))) {
    return fail('"exemptTypes" muss eine Liste von Texten sein.');
  }
  const exemptTypes = ((ex as string[] | undefined) ?? [])
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

  return {
    enabled: m.enabled,
    start,
    end,
    message: str("message"),
    bypassKey: str("bypassKey"),
    ...(exemptTypes.length ? { exemptTypes } : {}),
  };
}

/** Reine Auswertung zum Zeitpunkt `now`. */
export function resolveMaintenance(
  cfg: MaintenanceConfig | undefined,
  now: Date,
  bypassed = false
): MaintenanceState {
  const message = cfg?.message ?? DEFAULT_MESSAGE;
  const exemptTypes = cfg?.exemptTypes ?? [];
  const inactive: MaintenanceState = { active: false, message, exemptTypes };
  if (!cfg || !cfg.enabled || bypassed) return inactive;
  if (cfg.start && now < cfg.start) return inactive;
  if (cfg.end && now >= cfg.end) return inactive;
  return { active: true, message, end: cfg.end, exemptTypes };
}

/** True, wenn die Meldung für diesen meterreading-Typ greift. */
export function maintenanceAppliesTo(state: MaintenanceState, type: string): boolean {
  return state.active && !state.exemptTypes.includes(type.trim().toLowerCase());
}

/**
 * True, wenn die aktuelle Session den Bypass-Schlüssel kennt. Ein passender
 * URL-Parameter wird in sessionStorage gemerkt, damit er Navigationen und
 * Reloads innerhalb des Tabs übersteht.
 */
export function isMaintenanceBypassed(
  cfg: MaintenanceConfig | undefined,
  search: string = typeof window === "undefined" ? "" : window.location.search
): boolean {
  const key = cfg?.bypassKey;
  if (!key) return false;
  const fromUrl = new URLSearchParams(search).get(BYPASS_PARAM);
  try {
    if (fromUrl === key) {
      sessionStorage.setItem(BYPASS_STORAGE_KEY, key);
      return true;
    }
    return sessionStorage.getItem(BYPASS_STORAGE_KEY) === key;
  } catch {
    // sessionStorage gesperrt (Privatmodus o. Ä.): dann gilt nur die URL.
    return fromUrl === key;
  }
}
